import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { jsPDF } from 'jspdf'
import { getCaseReport } from '../services/api'
import OsteoLogo from '../components/Logo'

// ── Helpers ───────────────────────────────────────────────────────
function formatBytes(bytes) {
  if (!bytes || bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  })
}

// ── Status colour helpers ─────────────────────────────────────────
const STATUS_STYLES = {
  PASSED:  { pill: 'bg-green-100 text-green-700',  dot: 'bg-green-500',  label: 'Valid'    },
  WARNING: { pill: 'bg-yellow-100 text-yellow-700', dot: 'bg-yellow-400', label: 'Warning'  },
  FAILED:  { pill: 'bg-red-100 text-red-600',       dot: 'bg-red-500',    label: 'Invalid'  },
  PENDING: { pill: 'bg-gray-100 text-gray-500',     dot: 'bg-gray-400',   label: 'Pending'  },
}

function statusStyle(s) {
  return STATUS_STYLES[s] ?? STATUS_STYLES.PENDING
}

// ── PDF generation ────────────────────────────────────────────────
async function downloadPDF(data) {
  // Fetch logo and convert to data URL for embedding in PDF
  let logoDataUrl = null
  try {
    const res  = await fetch('/osteo3d-logo.png')
    const blob = await res.blob()
    logoDataUrl = await new Promise((resolve, reject) => {
      const reader   = new FileReader()
      reader.onload  = () => resolve(reader.result)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  } catch {
    // Logo unavailable — continue without it
  }

  const doc   = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  const M     = 18               // margin
  const PW    = 210              // page width
  let   y     = M

  const newPage   = () => { doc.addPage(); y = M }
  const checkY    = (need = 10) => { if (y + need > 268) newPage() }  // footer starts at ~274
  const colA      = M            // Check name
  const colB      = M + 44       // Value
  const colC      = M + 84       // Status
  const colD      = M + 104      // Message

  // ── Logo (header, page 1 only) ───────────────────────────────
  if (logoDataUrl) {
    doc.addImage(logoDataUrl, 'PNG', PW - M - 40, y - 8, 40, 13)
  }

  // ── Title ───────────────────────────────────────────────────
  doc.setFontSize(18)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(15, 23, 42)
  doc.text('DICOM Validation Report', M, y)
  y += 8

  doc.setFontSize(9)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(80)
  doc.text(`Case ID: ${data.caseId}`, M, y)
  doc.text(
    `Generated: ${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}`,
    PW - M, y, { align: 'right' },
  )
  y += 5
  doc.setDrawColor(200)
  doc.line(M, y, PW - M, y)
  y += 8

  // ── Summary ─────────────────────────────────────────────────
  const total   = data.uploads.length
  const passed  = data.uploads.filter((u) => u.validationStatus === 'PASSED').length
  const warning = data.uploads.filter((u) => u.validationStatus === 'WARNING').length
  const failed  = data.uploads.filter((u) => u.validationStatus === 'FAILED').length
  const pending = data.uploads.filter((u) => u.validationStatus === 'PENDING').length

  doc.setFontSize(11)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(0)
  doc.text('Summary', M, y)
  y += 6

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  doc.setTextColor(60)
  doc.text(
    `${total} file${total !== 1 ? 's' : ''}   ·   ${passed} passed   ·   ${warning} warnings   ·   ${failed} failed${pending > 0 ? `   ·   ${pending} pending` : ''}`,
    M, y,
  )
  y += 10

  // ── Per-file sections ────────────────────────────────────────
  for (const upload of data.uploads) {
    checkY(28)

    // File header
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(10)
    doc.setTextColor(15, 23, 42)
    const nameTrunc = upload.filename.length > 62 ? upload.filename.slice(0, 59) + '…' : upload.filename
    doc.text(nameTrunc, M, y)

    const sc = upload.validationStatus === 'PASSED'  ? [22, 163, 74]
              : upload.validationStatus === 'WARNING' ? [161, 99, 8]
              : upload.validationStatus === 'FAILED'  ? [220, 38, 38]
              : [100, 100, 100]
    doc.setTextColor(...sc)
    doc.text(statusStyle(upload.validationStatus).label, PW - M, y, { align: 'right' })
    doc.setTextColor(0)
    y += 5

    doc.setFont('helvetica', 'normal')
    doc.setFontSize(8)
    doc.setTextColor(100)
    doc.text(`${formatBytes(upload.fileSizeBytes)}   ·   Uploaded ${formatDate(upload.uploadedAt)}`, M, y)
    y += 5

    const rj = upload.validationReportJson
    if (rj?.checks?.length) {
      // Column headers
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(7.5)
      doc.setTextColor(100)
      doc.text('Check',   colA, y)
      doc.text('Value',   colB, y)
      doc.text('Result',  colC, y)
      doc.text('Message', colD, y)
      y += 3
      doc.setDrawColor(200)
      doc.line(M, y, PW - M, y)
      y += 3

      doc.setFont('helvetica', 'normal')
      for (const check of rj.checks) {
        checkY(6)
        const rc = check.status === 'PASSED'  ? [22, 163, 74]
                 : check.status === 'WARNING' ? [161, 99, 8]
                 : [220, 38, 38]
        doc.setFontSize(8)
        doc.setTextColor(30)
        doc.text(check.name, colA, y)
        doc.text(String(check.value ?? '—'), colB, y)
        doc.setTextColor(...rc)
        doc.text(check.status, colC, y)
        doc.setTextColor(60)
        const note = doc.splitTextToSize(check.message || '', 70)
        doc.text(note, colD, y)
        y += Math.max(5, note.length * 4)
      }
    } else {
      doc.setFont('helvetica', 'italic')
      doc.setFontSize(8)
      doc.setTextColor(150)
      doc.text('No validation data available for this file.', M, y)
      y += 5
    }

    y += 4
    doc.setDrawColor(220)
    doc.line(M, y, PW - M, y)
    y += 7
  }

  // ── Footer on every page ─────────────────────────────────────
  const copyright =
    'Copyright \u00A9 2026 Osteo3d\u00AE. All rights reserved. ' +
    '\u2018Osteo3d\u00AE\u2019 and \u20183d printing healthcare\u00AE\u2019 are registered trademarks. ' +
    'Unauthorized use/replication is prohibited. ' +
    'We do not have any representatives or distributors.'

  const totalPages = doc.getNumberOfPages()
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p)

    // Separator line
    doc.setDrawColor(210)
    doc.line(M, 274, PW - M, 274)

    // Logo (footer, right side)
    if (logoDataUrl) {
      doc.addImage(logoDataUrl, 'PNG', PW - M - 30, 276, 30, 9)
    }

    // Portal label + page number
    doc.setFontSize(7)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(120)
    doc.text('Osteo3d Medical Data Portal  \u00B7  Confidential', M, 280)
    doc.text(`Page ${p} of ${totalPages}`, PW - M - 32, 280, { align: 'right' })

    // Copyright notice
    doc.setFontSize(6)
    doc.setTextColor(160)
    const copyrightLines = doc.splitTextToSize(copyright, PW - M * 2)
    doc.text(copyrightLines, M, 285)
  }

  doc.save(`dicom-report-${data.caseId}.pdf`)
}

// ── Main page ─────────────────────────────────────────────────────
export default function ValidationReport() {
  const { caseId } = useParams()
  const navigate   = useNavigate()

  const [loading,    setLoading]    = useState(true)
  const [error,      setError]      = useState(null)
  const [report,     setReport]     = useState(null)
  const [expandedId, setExpandedId] = useState(null)   // accordion

  useEffect(() => {
    getCaseReport(caseId)
      .then(({ data }) => {
        setReport(data)
        // Auto-expand the first failed file if any
        const firstFail = data.uploads.find((u) => u.validationStatus === 'FAILED')
        if (firstFail) setExpandedId(firstFail.id)
      })
      .catch((err) => {
        const msg = err.response?.data?.error || 'Failed to load report.'
        setError(msg)
      })
      .finally(() => setLoading(false))
  }, [caseId])

  // ── Loading ─────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Spinner className="w-8 h-8 text-brand-600 mx-auto" />
          <p className="mt-3 text-gray-500 text-sm">Loading report…</p>
        </div>
      </div>
    )
  }

  // ── Error ───────────────────────────────────────────────────
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="card max-w-sm w-full text-center">
          <p className="text-red-600 font-medium mb-1">{error}</p>
          <button onClick={() => navigate('/')} className="btn-secondary text-sm mt-3">
            Go home
          </button>
        </div>
      </div>
    )
  }

  // ── Derived stats ───────────────────────────────────────────
  const total   = report.uploads.length
  const passed  = report.uploads.filter((u) => u.validationStatus === 'PASSED').length
  const warning = report.uploads.filter((u) => u.validationStatus === 'WARNING').length
  const failed  = report.uploads.filter((u) => u.validationStatus === 'FAILED').length

  const overallStatus = failed > 0 ? 'FAILED' : warning > 0 ? 'WARNING' : 'PASSED'

  // ── Main UI ─────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50">

      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-3">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <OsteoLogo size="sm" />
            <div className="border-l border-gray-200 pl-4">
              <p className="text-sm font-semibold text-gray-700 leading-none">Validation Report</p>
              <p className="text-xs text-gray-400 font-mono mt-0.5">Case ID: {caseId}</p>
            </div>
          </div>
          <button onClick={() => navigate('/')} className="text-xs text-gray-400 hover:text-gray-600">
            ← Back
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8 space-y-5">

        {/* Summary card */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs text-gray-400 mb-1">Overall result</p>
              <OverallBadge status={overallStatus} />
            </div>
            <button
              onClick={() => downloadPDF(report).catch(err => console.error('PDF generation error:', err))}
              className="flex items-center gap-2 btn-secondary text-sm"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h4a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
              </svg>
              Download PDF
            </button>
          </div>

          <div className="grid grid-cols-4 gap-3">
            <StatBox label="Total" value={total} colour="text-gray-700" />
            <StatBox label="Passed"   value={passed}  colour="text-green-600" />
            <StatBox label="Warnings" value={warning} colour="text-yellow-600" />
            <StatBox label="Failed"   value={failed}  colour="text-red-600" />
          </div>

          <p className="text-xs text-gray-400 mt-3">
            Case created {formatDate(report.createdAt)}
          </p>
        </div>

        {/* Empty state */}
        {total === 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <p className="text-gray-500 font-medium">No files uploaded yet for this case.</p>
            <button
              onClick={() => navigate(`/upload/${caseId}`)}
              className="btn-primary text-sm mt-4"
            >
              Upload files
            </button>
          </div>
        )}

        {/* File list */}
        {report.uploads.map((upload) => {
          const isOpen   = expandedId === upload.id
          const rj       = upload.validationReportJson
          const hasChecks = rj?.checks?.length > 0
          const st       = statusStyle(upload.validationStatus)

          return (
            <div
              key={upload.id}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden"
            >
              {/* File row — click to expand */}
              <button
                className="w-full px-5 py-4 flex items-center gap-3 text-left hover:bg-gray-50 transition-colors"
                onClick={() => setExpandedId(isOpen ? null : upload.id)}
              >
                {/* Status dot */}
                <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${st.dot}`} />

                {/* Filename + meta */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{upload.filename}</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {formatBytes(upload.fileSizeBytes)} · Uploaded {formatDate(upload.uploadedAt)}
                  </p>
                </div>

                {/* Status pill */}
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full flex-shrink-0 ${st.pill}`}>
                  {st.label}
                </span>

                {/* Chevron */}
                {hasChecks && (
                  <svg
                    className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                )}
              </button>

              {/* Expanded checks */}
              {isOpen && hasChecks && (
                <div className="border-t border-gray-100">
                  {rj.meta?.seriesDescription || rj.meta?.studyDate ? (
                    <div className="px-5 py-2.5 bg-gray-50 border-b border-gray-100 flex gap-4 text-xs text-gray-500">
                      {rj.meta.seriesDescription && (
                        <span>Series: <span className="text-gray-700 font-medium">{rj.meta.seriesDescription}</span></span>
                      )}
                      {rj.meta.studyDate && (
                        <span>Study date: <span className="text-gray-700 font-medium">{formatStudyDate(rj.meta.studyDate)}</span></span>
                      )}
                    </div>
                  ) : null}

                  {/* Check rows */}
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 text-gray-400 uppercase tracking-wide text-[10px]">
                        <th className="px-5 py-2 text-left font-medium">Check</th>
                        <th className="px-3 py-2 text-left font-medium">Value</th>
                        <th className="px-3 py-2 text-left font-medium">Expected</th>
                        <th className="px-3 py-2 text-left font-medium">Result</th>
                        <th className="px-3 py-2 text-left font-medium">Notes</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {rj.checks.map((check, i) => {
                        const cs = statusStyle(check.status)
                        return (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="px-5 py-2.5 font-medium text-gray-700 whitespace-nowrap">{check.name}</td>
                            <td className="px-3 py-2.5 font-mono text-gray-600">{check.value ?? '—'}</td>
                            <td className="px-3 py-2.5 text-gray-400">{check.expected}</td>
                            <td className="px-3 py-2.5">
                              <span className={`inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${cs.pill}`}>
                                {check.status}
                              </span>
                            </td>
                            <td className="px-3 py-2.5 text-gray-500">{check.message}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* No validation data */}
              {isOpen && !hasChecks && (
                <div className="border-t border-gray-100 px-5 py-4 text-xs text-gray-400 italic">
                  No validation data available for this file.
                </div>
              )}
            </div>
          )
        })}

      </main>
    </div>
  )
}

// Converts DICOM study date "20240115" → "15 Jan 2024"
function formatStudyDate(raw) {
  if (!raw || raw.length !== 8) return raw
  const y = raw.slice(0, 4), m = raw.slice(4, 6), d = raw.slice(6, 8)
  return new Date(`${y}-${m}-${d}`).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  })
}

function StatBox({ label, value, colour }) {
  return (
    <div className="text-center bg-gray-50 rounded-lg py-3">
      <p className={`text-2xl font-bold ${colour}`}>{value}</p>
      <p className="text-xs text-gray-400 mt-0.5">{label}</p>
    </div>
  )
}

function OverallBadge({ status }) {
  const map = {
    PASSED:  { cls: 'bg-green-100 text-green-800',  icon: 'M5 13l4 4L19 7',       label: 'All checks passed'            },
    WARNING: { cls: 'bg-yellow-100 text-yellow-800', icon: 'M12 9v2m0 4h.01',      label: 'Passed with warnings'         },
    FAILED:  { cls: 'bg-red-100 text-red-700',       icon: 'M6 18L18 6M6 6l12 12', label: 'One or more files need review' },
  }
  const { cls, icon, label } = map[status] ?? map.PASSED
  return (
    <span className={`inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-full ${cls}`}>
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d={icon} />
      </svg>
      {label}
    </span>
  )
}

function Spinner({ className = 'w-5 h-5' }) {
  return (
    <svg className={`${className} animate-spin`} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
  )
}
